import { type ReactNode, useEffect, useRef, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { LinkButton } from "../components/ui/Button";
import { remember } from "../lib/searchHistory";
import { brand } from "../config/brand";
import { DesktopDownload } from "../features/desktop/DesktopDownload";
import { Wordmark } from "../components/ui/Wordmark";
import { copy } from "../config/copy";
import { MessagesIndicator } from "../features/messages/MessagesIndicator";
import { ActivityButton } from "../features/activity/ActivityDrawer";
import { NotificationBell } from "../features/notifications/NotificationBell";
import { PlayerBar, useDockVisible } from "../features/player/PlayerBar";
import { NowPlayingScreen } from "../features/player/NowPlayingScreen";
import { PlayerHost } from "../features/player/PlayerHost";
import { JamWatcher } from "../features/jam/useJam";
import { Notice } from "../components/ui/Notice";
import { OnAirButton } from "../features/rooms/OnAirButton";
import { RoomFollower } from "../features/rooms/RoomFollower";
import { StudioSession } from "../features/studio/StudioSession";
import { Tooltip } from "./Tooltip";
import { MutedBanner } from "../features/moderation/MutedBanner";
import { DockChat } from "../features/messages/DockChat";
import { SearchSuggest } from "../features/search/SearchSuggest";
import { LibraryDock } from "../features/library/LibraryDock";
import { DASHBOARD_PANES, useDesktop, useMediaQuery } from "../lib/viewport";
import { DocumentTitle } from "../lib/title";
import { useSession } from "../stores/session";
import { drag, isDesktop, noDrag, titlebarInset } from "./desktop";
import { AccountMenu } from "./AccountMenu";

/**
 * The screens that get no chrome.
 *
 * Signing in, signing up and the two token screens are single-purpose pages
 * with one thing to do on them. A header offering Discover and a dock offering
 * playback are both invitations to abandon the form, and the header's own
 * "Sign in" button on the sign-in page is faintly absurd.
 *
 * `verify-email` and `reset-password` are here too even though both are
 * reachable while signed in: they are arrived at from an email, to do one
 * thing, and then leave.
 */
const BARE = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  // Both halves of signing in through a provider: choosing a username, and the
  // page the system browser lands on when the desktop app sent it.
  "/finish-signup",
  "/desktop-return",
];

/**
 * Screens that own the whole viewport, and so get no footer.
 *
 * The console and Messages are sized to exactly the space between the header
 * and the dock, and both go to some trouble not to scroll - a footer beneath
 * them adds its own height below the fold and hands the page a scrollbar back,
 * which is the precise thing that sizing exists to remove.
 *
 * A prefix match rather than equality, because `/messages/:id` is the same
 * screen as `/messages`.
 */
const FULL_HEIGHT = ["/console", "/messages", "/master", "/jam"];

/**
 * Screens with no dock at all: the strip is not drawn and nothing
 * reserves space for it.
 *
 * The mastering desk is a tool with its own transport, and a second one
 * under it playing something else would be two songs at once. The desk
 * pauses the site player on the way in; the site player comes back the
 * moment you leave.
 */
const DOCKLESS = ["/master"];

/**
 * No site footer under the admin panel. It is a workbench with its own rail
 * of sections; the public footer's links to terms and the press kit under a
 * table of reports read as part of the table.
 */
const FOOTERLESS = ["/admin"];

/**
 * And the dashboard, but only for somebody signed in.
 *
 * `/` is one route rendering two different pages: a visitor gets a home that
 * scrolls and ends in a footer, while a signed-in reader gets three panes that
 * fill the viewport and scroll individually. A footer under that would be a
 * footer nobody can reach, and a scrolling document would drag all three panes
 * at once - which is the behaviour the panes exist to stop.
 */
const FULL_HEIGHT_SIGNED_IN = ["/"];

/**
 * The frame every page renders inside.
 *
 * ## The rule this file exists to protect
 *
 * `<DockBar />` is a sibling of `<Outlet />`, not a child of it. React keeps a
 * component mounted as long as its position in the tree is stable, so a bar
 * outside the router outlet survives every navigation - and the single
 * `<audio>` element that will live inside it survives with it.
 *
 * That is the entire mechanism behind "playback never stops when you navigate"
 * (HANDOFF §9.1). There is no clever state-preservation trick; there is only
 * this structural fact. Anything that moves the dock inside a route, or wraps
 * the outlet in something that remounts, silently breaks the headline feature
 * in a way tests rarely catch.
 */
export function AppShell() {
  const { pathname } = useLocation();
  // Clearance is reserved only when there is something to clear.
  /*
   * Clearance is owed to the *strip*, which is now the player or chat.
   *
   * Reserving it from `useDockVisible` alone left the last two centimetres of
   * every page underneath a docked conversation on a live profile - the one
   * case where the player is hidden and the bar is not.
   */
  const dock = useDockPresent();
  const bare = BARE.includes(pathname);
  const signedIn = useSession((state) => state.user !== null);
  /*
   * The dashboard owns the viewport only when it is actually in its paned
   * layout - signed in *and* wide enough. Below that it is an ordinary
   * scrolling page and wants its footer and its dock clearance back.
   *
   * The two used to disagree: the shell dropped both the moment somebody signed
   * in, while the panes waited for 1536, so every window in between rendered a
   * scrolling page whose last rows sat under the dock with no footer at all.
   */
  const paned = useMediaQuery(DASHBOARD_PANES);
  const fullHeight =
    FULL_HEIGHT.some((path) => pathname === path || pathname.startsWith(path + "/"))
    || (signedIn && paned && FULL_HEIGHT_SIGNED_IN.includes(pathname));
  const footerless = FOOTERLESS.some((path) => pathname === path || pathname.startsWith(path + "/"));

  return (
    /*
      The studio's engine, wrapping everything for the same structural reason
      the audio element is down there: outside the outlet, so the router cannot
      tear it down. A broadcast used to end when the host navigated to a
      profile. It renders nothing and does nothing until the console opens it.
    */
    <StudioSession>
      {/*
        Writes the document title from whatever page has claimed it. Rendered
        here so it survives navigation, and returns null so it costs a tab's
        name and nothing else. See `lib/title.ts` on why the pages do not write
        `document.title` themselves.
      */}
      <DocumentTitle />

      <div className="flex min-h-full flex-col bg-app text-ink">
        {!bare && <TopBar />}

        {/*
        Two things at once.

        Bottom padding equal to the dock's height, or the last item of every
        scrollable page sits permanently behind the bar - the most common bug in
        this layout, and it reads as a content problem rather than a layout one.

        And centred on the bare screens, where the header and dock are both gone
        and a form pinned to the top of an empty viewport looks like a page that
        failed to finish loading.
      */}
        {!bare && <MutedBanner />}

        <main
          className={
            bare
              ? "flex flex-1 items-center justify-center"
              /*
                A full-height page reserves its own dock clearance in the
                height it asks for, so adding padding here would push it that
                far past the bottom of the window and reintroduce the scrollbar
                it went to some trouble to avoid.
              */
              : fullHeight
                ? "flex-1"
                : dock
                  ? "flex-1 pb-[calc(var(--spacing-dock)+1.5rem)]"
                  : "flex-1 pb-6"
          }
        >
          <Outlet />
        </main>

        {!bare && !fullHeight && !footerless && <SiteFooter />}

        {!bare && <DockBar />}

        {/*
          A sibling of the dock, not a wrapper around it. The single `<audio>`
          element lives down there and everything about playback surviving a
          navigation depends on it never moving - so the full-screen view reads
          the same store and draws a bigger picture of it, rather than taking
          the player inside itself.
        */}
        <NowPlayingScreen />

        {/*
        Outside `bare`, unlike the dock and the footer.

        Auth pages have `title`s too, and a tooltip is chrome in the way a
        navigation bar is not - there is nothing about a sign-in screen that
        wants the native one back.
      */}
        <Tooltip />

        {/*
        Keeps a followed broadcast alive across navigation, for the same
        structural reason the audio element lives here: outside the outlet,
        so the router cannot tear it down. Renders nothing unless somebody is
        actually listening to a room.
      */}
        <RoomFollower />

        {/*
        The single <audio> element, mounted once here and never unmounted.
        Renders nothing; PlayerBar above is the UI. See the note in PlayerHost
        for why the two are separate.

        Outside the `bare` check on purpose. Hiding the dock on an auth screen
        hides the *controls*; unmounting this would stop the audio, so somebody
        who opened a password-reset link mid-track would have the music cut out
        on them. The sound keeps playing and the dock reappears on the way out.
      */}
        <PlayerHost />
        {/* Listens to the jam this person is in, if any, and keeps the
            player's queue following it. Renders nothing. */}
        <JamWatcher />
        <Notice />
      </div>
    </StudioSession>
  );
}

function TopBar() {
  const user = useSession((s) => s.user);

  return (
    <header className="sticky top-0 z-20 border-b border-line bg-deep/95 backdrop-blur">
      {/*
        Full width, not capped.

        Chrome spans the viewport; content is centred within it. Capping the bar
        at 1500px gave three different alignments on a wide monitor - the bar at
        1500, a page's column at 768, and Messages edge to edge - and none of
        them lined up with each other.

        The gutters stay, so nothing touches the screen edge.
      */}
      {/*
        In the desktop shell this strip is the title bar.

        `drag` makes the window follow the pointer from here; every interactive
        thing inside then has to opt out with `noDrag` or it stops responding
        while still looking like a control. The padding is for the system's own
        window buttons, which are drawn over one end of this bar and differ by
        platform - see `app/desktop.ts`.

        In a browser all three are empty strings and this is the header it
        always was.
      */}
      <div
        /*
          Tighter below `sm`. At 390px the wordmark, the sign-in link and the
          join button came to seven pixels more than the screen, and since the
          buttons do not shrink it was the button that went over the edge.
        */
        className={`flex h-16 items-center gap-3 px-4 sm:gap-5 sm:px-6 ${drag}`}
        style={
          isDesktop
            ? {
                paddingLeft: `max(1.5rem, ${titlebarInset.left}px)`,
                paddingRight: `max(1.5rem, ${titlebarInset.right}px)`,
              }
            : undefined
        }
      >
        <Link
          to="/"
          className={`flex shrink-0 items-center ${noDrag}`}
          aria-label={brand.name}
        >
          {/*
            The lockup, not a mark plus a text node. Its two-tone treatment -
            GET in Signal, VIBED in Text - lives inside the component, so it
            cannot drift from the brand files (§1.1).

            Drawn rather than loaded, so its fills can be tokens: an SVG behind
            `<img>` cannot see them, and VIBED is the near-white half.
          */}
          {/*
            The full lockup where there is room, the mark alone where there is
            not. Below `sm` the name and the action cluster came to 456px on a
            390px screen, and both refuse to shrink - so the bar overflowed and
            took "Drop a track" over the edge with it.
          */}
          <Wordmark className="hidden h-8 w-auto sm:block" />
          <Wordmark markOnly className="h-8 w-auto sm:hidden" />
        </Link>

        {/*
          The wrapper carries the flex sizing, not just the drag opt-out.
 
          Adding this div for the desktop title bar quietly capped the search
          box: the form inside it grows, but the div became the flex child and
          sized itself to its content, so the field stayed about 260 pixels wide
          at every window size. The classes it needs are the ones the form used
          to have.

          Including the width limit. The form is `max-w-xl` and the wrapper was
          not, so on a wide screen the wrapper kept growing behind a field that
          had stopped - leaving several hundred pixels of empty flex child
          between the visible search box and the live badge that is supposed to
          sit against it. The badge was in the right place in the markup and
          nowhere near it on screen.
        */}
        <div className={`hidden min-w-0 max-w-xl flex-[3] md:block ${noDrag}`}>
          <SearchBar />
        </div>

        {/*
          The live count, immediately after the search box.

          It used to be centred inside `flex-1` - the leftover space between the
          search box and the nav - which meant its position was a side effect of
          how wide the field happened to be. Widening the search moved the pill
          off centre, and it would have moved again at the next breakpoint.

          `shrink-0` beside the field, with the gap doing the spacing: it now
          sits at the end of the search box wherever that ends up, and the nav
          keeps the right-hand side. Nothing is centred on a leftover.

          Renders nothing when nobody is on air, which is most of the time. See
          `OnAirButton` on why a permanent "0 live" would be worse than absent.
        */}
        <div className={`hidden shrink-0 md:flex md:items-center ${noDrag}`}>
          <OnAirButton />
        </div>

        {/* Pushes the nav to the right now that nothing else is stretching. */}
        <div className="flex-1" aria-hidden />

        <nav className={`hidden items-center gap-1 lg:flex ${noDrag}`}>
          <NavItem to="/discover">{copy["nav.discover"]}</NavItem>

          {/*
            For hosts, and only for somebody who is not one yet.

            It is a page about a decision - what running a room here involves -
            and the two people who never need it are the host, who has the
            console, and the listener who has already signed up and can find the
            application in their account menu. So it is offered to the stranger,
            beside the only other thing up here.

            The route stays public either way: a host sending the link to a
            friend should not have it redirect out from under them.
          */}
          {!user && <NavItem to="/for-hosts">{copy["nav.hosts"]}</NavItem>}

          {/*
            Discover is the only thing left up here, which is deliberate.

            Favourites went when the library block arrived at the left of the
            dock: this nav only renders at `lg`, comfortably above the `md`
            where that block appears, so there was nowhere the link could be
            seen that the dock was not already offering the same thing.

            Following went with it. Both are still in the account menu, which is
            also the route below `md`, where neither the dock nor this nav
            exists at all.

            Worth being precise about `/following`, because the two senses of
            the word are easy to conflate: this was the *feed* assembled from
            the people you follow. A profile's "following" figure goes to
            `/{username}/following`, which is the list of those people - a
            different page. The feed's remaining door is the account menu.

            Rooms (Phase 7) is still unbuilt. Its string stays in `copy.ts`
            because the vocabulary is settled; the link is absent because a nav
            item that leads to "Nothing here" teaches people the site is broken.
          */}
        </nav>

        {user ? (
          /*
            Three things, in the order they are wanted: what is new, the thing
            you came to do, and everything about your account behind one door.

            Admin, the profile link and sign out used to sit out here. They are
            in AccountMenu now - see the note there on why a permanent sign-out
            button next to the primary action is the wrong shape.
          */
          <div
            className={`ml-auto flex shrink-0 items-center gap-2 lg:ml-0 ${noDrag}`}
          >
            <ActivityButton />
            <MessagesIndicator />
            <NotificationBell />

            {/*
              The label goes only where it genuinely will not fit.

              Below 360px - the 2016 SE and little else - the header's controls
              come to more than the screen, and this is the widest of them. The
              breakpoint is exact rather than Tailwind's `sm`, because `sm` is
              640 and would strip the label from every phone in order to fix one
              that almost nobody carries. The accessible name stays either way.
            */}
            <LinkButton to="/upload" aria-label={copy["cta.upload"]}>
              <span className="max-[359px]:hidden">{copy["cta.upload"]}</span>
              <span className="hidden max-[359px]:inline" aria-hidden>
                +
              </span>
            </LinkButton>

            <AccountMenu />
          </div>
        ) : (
          /*
            `lg:ml-0`, matching the signed-in block above.

            Two siblings both carrying `ml-auto` split the free space between
            them, which is how a signed-out header ended up with "Discover"
            stranded in the middle of the bar: the nav claimed half the gap and
            the buttons the other half. Above `lg` the nav's own `ml-auto` does
            all the pushing and everything to its right sits flush against it;
            below `lg` the nav is hidden, so these need it back.
          */
          <div
            className={`ml-auto flex shrink-0 items-center gap-2 lg:ml-0 ${noDrag}`}
          >
            <LinkButton to="/login" variant="ghost">
              {copy["cta.login"]}
            </LinkButton>
            <LinkButton to="/register">{copy["cta.signup"]}</LinkButton>
          </div>
        )}
      </div>
    </header>
  );
}

function NavItem({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `rounded-[var(--radius-pill)] px-3 py-2 text-sm transition-colors ${
          isActive ? "text-ink" : "text-soft hover:text-ink"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

/**
 * Search, with `/` to focus it.
 *
 * The shortcut appears in the design as a visible hint at the right of the
 * field, and it is the one keyboard affordance worth having before the
 * player's: on a discovery-led site, search is what people reach for most.
 *
 * Guarded against firing while somebody is already typing - without that check,
 * pressing `/` mid-sentence in a bio steals focus and eats the character.
 */
/**
 * The header search.
 *
 * Submits to `/search`, which owns the query in its URL - so results are
 * shareable and the back button behaves. This box only starts the search; it
 * deliberately does not keep its own copy of the term, because two places
 * holding the query is how the box and the results drift apart.
 */
function SearchBar() {
  const input = useRef<HTMLInputElement>(null);
  const wrapper = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  /*
   * The term is tracked here now, though the input is still uncontrolled.
   *
   * The suggestions need to know what has been typed, and `defaultValue` seeded
   * from the URL is what makes landing on a results page show the query that
   * produced it. Reading the value on change keeps both: React never sets it,
   * and the dropdown still follows it.
   */
  const [term, setTerm] = useState(params.get("q") ?? "");
  const [open, setOpen] = useState(false);

  /* Clicking anywhere else puts the suggestions away. */
  useEffect(() => {
    if (!open) return;

    const away = (event: PointerEvent) => {
      if (wrapper.current?.contains(event.target as Node)) return;

      setOpen(false);
    };

    document.addEventListener("pointerdown", away);

    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "/") return;

      const active = document.activeElement;
      const typing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);

      if (typing) return;

      event.preventDefault();
      input.current?.focus();
    }

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <form
      ref={wrapper}
      role="search"
      /*
       * Full width of its wrapper, which is where the limit lives.
       *
       * That limit is `max-w-xl` and it is deliberate: `max-w-md` was sized for
       * a box somebody types into and nothing more, and with suggestions
       * underneath it a track title and the artist beneath it were truncating
       * at once in a list with plenty of vertical room and none across.
       *
       * Capped on the wrapper rather than here, so the flex child ends where
       * the field ends and whatever sits beside it sits beside the field.
       */
      className="relative w-full"
      onSubmit={(event) => {
        event.preventDefault();

        const typed = input.current?.value.trim() ?? "";

        if (typed === "") return;

        /*
         * Remembered here, and on every other way a search ends - picking a
         * suggestion, or "See all results". Nothing is recorded while somebody
         * types, only when they act, so what lands in the list is always a
         * whole term rather than a prefix.
         */
        remember(typed);
        setOpen(false);
        navigate(`/search?q=${encodeURIComponent(typed)}`);
      }}
    >
      <span
        aria-hidden
        className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-faint"
      >
        &#9679;
      </span>
      <input
        ref={input}
        type="search"
        name="q"
        aria-label="Search"
        data-search-input
        // Uncontrolled, seeded from the URL. `defaultValue` with a key that
        // changes when the query does means landing on a results page shows
        // what was searched for, without this component owning the value.
        key={params.get("q") ?? ""}
        defaultValue={params.get("q") ?? ""}
        autoComplete="off"
        onChange={(event) => {
          setTerm(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Only what is actually searchable. Rooms and tags are not built, and a
        // placeholder that promises them is a bug report waiting to happen.
        placeholder="artists, tracks, albums"
        className="w-full rounded-[var(--radius-pill)] border border-line bg-slate py-2.5 pl-9 pr-12 text-sm text-ink placeholder:text-faint focus:border-line-strong"
      />
      <kbd className="meta pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-[var(--radius-chip)] border border-line px-1.5 text-faint">
        /
      </kbd>

      <SearchSuggest
        term={term}
        open={open}
        onClose={() => setOpen(false)}
        inputRef={input}
      />
    </form>
  );
}

/**
 * The bottom of every page.
 *
 * ## Built because a form nobody can reach is not a process
 *
 * The copyright notice form existed at `/dmca` and nothing linked to it, which
 * makes it a URL rather than a route into the takedown process (§11). Safe
 * harbour assumes a claimant can *find* how to complain; a page you have to be
 * told the address of does not qualify, and neither does one only our own users
 * could stumble on.
 *
 * Deliberately small. This is not a sitemap - it is the two or three things a
 * stranger needs and cannot get anywhere else, kept quiet enough not to compete
 * with the page above it.
 */
function SiteFooter() {
  return (
    /*
      Its own dock clearance.

      `main`'s bottom padding used to be the last thing on the page and kept
      content off the fixed dock; with a footer after it, that padding now sits
      *above* the footer and the footer is what lands underneath the bar. The
      clearance has to move with the bottom of the page.
    */
    <footer
      className={`border-t border-line px-6 pt-8 ${
        // Same clearance question as `main` above, asked where the page
        // actually ends.
        useDockPresent() ? "pb-[calc(var(--spacing-dock)+2rem)]" : "pb-8"
      }`}
    >
      <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-x-6 gap-y-2">
        <span className="meta text-ghost">
          © {new Date().getFullYear()} {brand.name}
        </span>

        {/* First, because it is the only footer link that changes. */}
        <Link
          to="/whats-new"
          className="meta text-soft transition-colors hover:text-ink"
        >
          What's new
        </Link>

        <Link
          to="/terms"
          className="meta text-soft transition-colors hover:text-ink"
        >
          Terms
        </Link>

        <Link
          to="/privacy"
          className="meta text-soft transition-colors hover:text-ink"
        >
          Privacy
        </Link>

        {/* Beside Terms and Privacy because it is the third document, and
            before the copyright form because reading what you granted comes
            before reporting somebody who took it. */}
        <Link
          to="/artist-rights"
          className="meta text-soft transition-colors hover:text-ink"
        >
          Your music
        </Link>

        <Link
          to="/dmca"
          className="meta text-soft transition-colors hover:text-ink"
        >
          Report a copyright claim
        </Link>

        <Link
          to="/discover"
          className="meta text-soft transition-colors hover:text-ink"
        >
          {copy["nav.discover"]}
        </Link>

        {/*
          Reaches the people the account menu cannot: somebody who has not
          signed in on this machine, which is most of the people who would want
          the app. Renders nothing in the desktop app, or before a release
          exists for this platform.
        */}
        <DesktopDownload variant="footer" />

        <Link
          to="/host"
          className="meta text-soft transition-colors hover:text-ink"
        >
          Host a room
        </Link>
      </div>
    </footer>
  );
}

/**
 * The persistent player and chat dock (§9.2).
 *
 * A placeholder until Phase 2 and Phase 4. It is here now, in its final
 * position in the tree, because the position is the load-bearing part - adding
 * the bar later is easy, discovering later that it has to move is not.
 */
/**
 * Whether the bar is on screen at all.
 *
 * The player and the chat rail come and go independently - a live profile hides
 * the transport and keeps the conversations - so the strip is present when
 * either is, and everything that has to clear it asks this rather than asking
 * about the player.
 */
function useDockPresent(): boolean {
  const player = useDockVisible();
  const user = useSession((state) => state.user);
  const { pathname } = useLocation();
  const dockless = DOCKLESS.some((path) => pathname === path || pathname.startsWith(path + "/"));

  /*
   * Asked here rather than left to `md:` alone.
   *
   * The rail hides itself with CSS below 768px, but the *strip* is a layout
   * box: were this to answer yes on a phone, every page would reserve 72px at
   * the bottom for a bar holding nothing whenever the player was also away.
   */
  const desktop = useDesktop();

  // After every hook, so the count is the same on every render.
  if (dockless) return false;

  return player || (Boolean(user) && desktop);
}

function DockBar() {
  /*
   * The player comes and goes; the bar stays if chat is in it.
   *
   * The strip used to be all or nothing - hidden entirely on a live profile,
   * because a transport for audio the dock is not playing is a row of controls
   * for silence. That was right about the player and wrong about the bar: the
   * bar also holds conversations (§9.2), and those have nothing to do with
   * what is playing. Taking the whole thing away ended a chat because somebody
   * opened a broadcast.
   *
   * So the two are asked separately now, and the strip renders when either has
   * something to show.
   */
  const player = useDockVisible();

  if (!useDockPresent()) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-30 flex h-[var(--spacing-dock)] items-stretch border-t border-line bg-deep">
      {/*
        The library at the left end, chat at the right, the player between
        them - all three full height and divided by rules, so the strip reads
        as one bar rather than three things sharing an edge.
      */}
      <div className="flex shrink-0 items-stretch">
        <LibraryDock />
      </div>

      {/*
        The player takes the room, chat sits at the right-hand end - which is
        also what happens when the player is absent: the empty half keeps the
        rail where it was rather than sliding it across the bar.
      */}
      <div className="min-w-0 flex-1">{player && <PlayerBar />}</div>

      {/*
        Flush to the right edge and full height: the rail is a section of the
        bar, divided from the player by a rule, rather than something resting
        on top of it.
      */}
      <div className="flex shrink-0 items-stretch">
        <DockChat />
      </div>
    </div>
  );
}
